import { Grid, Box } from '@mui/material';
import { setActiveView, updateSidebarItems } from 'features/applicationState';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState, SidebarItem } from 'types/types';
import NIService, { INIService } from 'services/NIService';
import UserManager from 'utils/UserManager';
import { generateQueryParams, getSidebarItemProp } from 'utils/Utils';
import { ReactComponent as PinSvg } from 'assets/images/pin.svg';
import { ReactComponent as UnpinSvg } from 'assets/images/unpin.svg';
import AIAssistant from 'components/AIAssistant/AIAssistant';
import { getCustomCss } from 'utils/customCss';

function LandingPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const appId = queryParams.get('selectedApp') || '';
  let subitemsCount = 0; 
  const customCss = getCustomCss(); 
  let folderName = customCss != null ? customCss + '/' : 'default/';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [animationClass, setAnimationClass] = useState<string>('');
  const [lastFavoriteItem, setLastFavoriteItem] = useState<string>('');
  const [animationClassFavorite, setAnimationClassFavorite] =
    useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null); // this used for the selected application
  
  // To track the active dashbord item (when we click to see the inner dashboards)
  type Item = {
    value: string;
    text: string;
    items?: Item[]; // Optional nested items
  };
  const [activeDashboardItems, setActiveDashboardItems] = useState<Item[]>([]);


  const { sidebarItems } = useSelector(
    (state: RootState) => state.applicationState
  );
  const [favoriteDashboardItems, setfavoriteDashboardItems] = useState<
    (SidebarItem | null)[]
  >([]);

  useEffect(() => {  
    const initialActiveIndex = sidebarItems.findIndex(item => item.value === appId);
    if (initialActiveIndex !== -1) {
      setActiveIndex(initialActiveIndex);
    } 
    else{
      setActiveIndex(0);
    }
    setTimeout(() => {
      setAnimationClass('show');
      setAnimationClassFavorite('show');
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []);

  useEffect(() => {  
    let favoriteDashboards: string[] = [];
    var userProperties = UserManager.getProperties();

    // check all favorite dashboards
    if (userProperties['favoriteDashboards']) {
      favoriteDashboards = userProperties['favoriteDashboards'].split(',');

      const processItemsRecursively = (items: SidebarItem[]): SidebarItem[] => {
        return items.map((item) => {
          const favDashIndex = favoriteDashboards.indexOf(item.value);
          const isFavorite = favDashIndex !== -1;
          const updatedItem: SidebarItem = { ...item, favorite: isFavorite };

          // If the item is a favorite, push it to favoriteDashboardItems
          if (isFavorite) {
            favoriteDashboardItems.push(updatedItem);
          }

          // If the item has nested items, process them recursively
          if (item.items && item.items.length > 0) {
            updatedItem.items = processItemsRecursively(item.items);
          }

          return updatedItem;
        });
      };

      const selectedAppItems = sidebarItems.map((appItem) => {
        const updatedItems = processItemsRecursively(appItem.items || []);
        return { ...appItem, items: updatedItems };
      }) as SidebarItem[];

      dispatch(updateSidebarItems(selectedAppItems));
    }

    dispatch(
      setActiveView({ viewId: 'landingPageView', title: 'Main menu' })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // arrow to slide up and down dashboards
  const openDashboard = (item) => { 
    const queryParams = generateQueryParams(item.properties);
    const vurl = item.value;
    const viewType = item.type.toLowerCase();
    navigate('/' + viewType + '/' + vurl + '?' + queryParams);
  };

  //get favorite dashboard background
  const getDashboardBackgroundImg = (item: SidebarItem | null) => {  
    // if (!item) return '';
    // return (
    //   'styles/images/dashboards/' + folderName +
    //   getSidebarItemProp(item, 'background', 'DashboardBg.png')
    // );
    return (
      'styles/images/dashboards/default/DashboardBg.png' 
    );
  };

  //get dashboard img
  const getDashboardIcon = (item: SidebarItem | null) => { 
    if (!item) return '';
    return 'styles/images/dashboardIcons/' + folderName + getSidebarItemProp(item, 'img', 'satellite.svg'); 
  };

  const handleDashboardItemClick = (item) => { 
    if (activeDashboardItems.some(activeItem => activeItem.value === item.value)) { 
      setActiveDashboardItems(activeDashboardItems.filter(activeItem => activeItem.value !== item.value));
    } else { 
      setActiveDashboardItems([...activeDashboardItems, item]);
    }
  };

  // menu application Click
  const applicationClick = (index, item) => {

    var externalLink = getSidebarItemProp(item, 'external_link', '');
    if (externalLink) {
      // Open the external link in a new tab
      window.open(externalLink, '_blank');
      return;
    }
    
    subitemsCount = 0;
    setActiveIndex(index);
    setAnimationClass('');

    setTimeout(() => {
      setAnimationClass('show');
    }, 100);
  };

  const getTopPinnedFolder = (index) => {
    // Calculate top position based on index and adjusted visible part
    const topPosition = index * 180;
    return {
      top: topPosition + 'px',
      zIndex: index,
    };
  };

  const markFavorites = async (item) => {
    setAnimationClassFavorite('');
    setfavoriteDashboardItems([]);

    const updateSidebarItemsRecursively = (
      items: SidebarItem[],
      itemToUpdate: SidebarItem
    ) => {
      return items.map((appItem) => {
        if (appItem?.items && appItem.items.length > 0) {
          const updatedItems = updateSidebarItemsRecursively(
            appItem.items,
            itemToUpdate
          );
          return { ...appItem, items: updatedItems };
        }
        setLastFavoriteItem(itemToUpdate.value);
        if (appItem.value === itemToUpdate.value) {
          if (!appItem.favorite) {
            setfavoriteDashboardItems([
              ...favoriteDashboardItems,
              appItem as SidebarItem,
            ]);
            return { ...appItem, favorite: true };
          } else {
            setfavoriteDashboardItems(
              favoriteDashboardItems.filter(
                (n) => n?.value !== itemToUpdate.value
              )
            );
            return { ...appItem, favorite: false };
          }
        }
        return appItem;
      });
    };

    // Usage:
    const updatedSelectedAppItems = updateSidebarItemsRecursively(
      sidebarItems,
      item
    );
    dispatch(updateSidebarItems(updatedSelectedAppItems)); 

    var favoriteDashboards: string[] = [];
    var userProperties = UserManager.getProperties();
    if (userProperties['favoriteDashboards'])
      favoriteDashboards = userProperties['favoriteDashboards'].split(',');

    var favDashIndex = favoriteDashboards.indexOf(item.value);
    if (favDashIndex === -1) favoriteDashboards.push(item.value);
    else favoriteDashboards.splice(favDashIndex, 1);

    var property = {
      Name: 'favoriteDashboards',
      Value: favoriteDashboards.join(),
    };
    const niService: INIService = new NIService();
    await niService.AddUserProerties(property);
    UserManager.setProperty(property.Name, property.Value);
   
    setTimeout(() => {
      setAnimationClassFavorite('show');
    }, 100);
  };

  return (
    <div id="main-area">
      <div id="main-area-landing-page" className="main-view-container">
        {/* main application list */}
        <Grid item xs={12}>
          <div className="landing-page-app-application-title">
            Network Insight Applications
          </div>
          <div className="main-area-application">
            {sidebarItems.map((item, index) => { 
              const isActive = index === activeIndex;
              return (
                <div
                  className={`application-items add-transition ${
                    isActive ? 'application-items-active' : ''
                  }`}
                  key={item.value}
                  onClick={() => applicationClick(index, item)}
                >
                  <div>
                    <img
                      width={43}
                      src={`styles/images/dashboardIcons/${folderName}${getSidebarItemProp(
                        item,
                        'img',
                        'statsbar-icon.svg'
                      )}`}
                      alt={item.text}
                    />
                  </div>
                  <div className="apllication-title">{item.text}</div>
                </div>
              );
            })}
          </div>
        </Grid>

        <Grid container spacing={4}>
          {/* dashboards list of selected application */}
          <Grid item xs={favoriteDashboardItems?.length > 0 ? 7 : 12}>
            <Grid
              id="main-area-landing-page-app-container"
              container
              spacing={4}
              columns={12}
              direction="row"
              justifyContent="flex-start"
              alignContent="center"
              alignItems="center"
            >
              {sidebarItems.map((item, index) => {
                if (index !== activeIndex) return null;
                var innerWidth = favoriteDashboardItems?.length > 0 ? 4 : 2;
                return (
                  <Grid item xs={12} alignContent="flex-start" key={item.value}>
                    <div>
                      <div className="main-area-landing-page-app-title title-db-application">
                        <div>
                          <img
                            src={`styles/images/dashboardIcons/${folderName}${getSidebarItemProp(
                              item,
                              'img',
                              'statsbar-icon.svg'
                            )}`}
                            alt={item.text}
                          />
                          {item.text}
                        </div>
                      </div>
                      <div className="menu-sub-items">
                        <Grid
                          id="landing-page-app-folder-dashboards"
                          container
                          spacing={4}
                          columns={12}
                          direction="row"
                          justifyContent="flex-start"
                          alignContent="center"
                          alignItems="stretch"
                        >
                          {item?.items?.map((subItem, subIndex) => {
                            if (subItem.items && subItem.items.length == 0) {
                              subitemsCount += 1;
                              return (
                                <Grid
                                  item
                                  xs={innerWidth}
                                  className={`custom-grid-class add-item-animation ${animationClass}`}
                                  alignContent="flex-start"
                                  key={subItem.value + subIndex}
                                  onClick={() => openDashboard(subItem)}
                                  style={{
                                    transitionDelay: `${subitemsCount * 100}ms`,
                                  }}
                                >
                                  <div className="item-spacing ">
                                    <div className="main-menu-item">
                                      <Box className="item-block-head">
                                        <div className="icon-dashboard">
                                          <img
                                            src={getDashboardIcon(subItem)}
                                            alt="open"
                                          />
                                        </div>
                                        <div className="main-area-landing-page-app-title-inner">
                                          {subItem.text}
                                        </div>
                                        <div
                                          className="addHover pinIcon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            markFavorites(subItem);
                                          }}
                                          title="#=data.favorite ? 'Unbookmark this dashboard': 'Bookmark this dashboard'#"
                                        >
                                          {subItem.favorite ? (
                                            <PinSvg />
                                          ) : (
                                            <UnpinSvg />
                                          )}
                                        </div>
                                      </Box>
                                    </div>
                                  </div>
                                </Grid>
                              );
                            } else {
                              subitemsCount += 1;
                              return (
                                <Grid
                                  item
                                  xs={innerWidth}
                                  className={`custom-grid-class add-item-animation ${animationClass} ${activeDashboardItems.some(activeItem => activeItem.value === subItem.value) ? 'dbActive' : ''}`} // Conditionally add active class
                                  alignContent="flex-start"
                                  key={subItem.value + subIndex}
                                  onClick={() => handleDashboardItemClick(subItem)}
                                  style={{
                                    transitionDelay: `${subitemsCount * 100}ms`,
                                  }}
                                >
                                  <div className="item-spacing ">
                                    <div className="main-menu-item add-transition">
                                      <Box className="item-block-head">
                                        <div className="icon-dashboard add-transition">
                                          <img
                                            src={getDashboardIcon(subItem)}
                                            alt="open"
                                            className="add-transition"
                                          />
                                        </div>
                                        <div className="main-area-landing-page-app-title-inner add-transition">
                                           <div className="relativeTitle">{subItem.text}<span className="add-arrow"></span></div>
                                        </div>   
                                        <div className="innerItems add-transition">
                                          {subItem.items &&
                                            subItem.items.map((nestedItem, nestedIndex) => (
                                              <div  key={`${nestedItem.value}-${nestedIndex}`} className='hoverPinIcon'>
                                               <div className="icon-dashboard-inner">
                                                  <img
                                                    src="styles/images/applications.svg"
                                                    alt="open" 
                                                  />
                                                </div>
                                                <div 
                                                  className="nested-item"
                                                  onClick={() => openDashboard(nestedItem)}
                                                >
                                                  {nestedItem.text} 
                                                </div> 
                                                <div
                                                    className="addHover pinIcon2"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      markFavorites(nestedItem);
                                                    }}
                                                    title={`${
                                                      nestedItem.favorite
                                                        ? 'Unbookmark this dashboard'
                                                        : 'Bookmark this dashboard'
                                                    }`}
                                                  >
                                                    {nestedItem.favorite ? (
                                                      <PinSvg width={18} />
                                                    ) : (
                                                      <UnpinSvg width={18} />
                                                    )}
                                                  </div>
                                              </div>
                                            ))}
                                        </div> 
                                      </Box>
                                    </div>
                                  </div>
                                </Grid>
                              );
                            }
                          })}
                        </Grid>
                      </div>
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
          {/* favorite pinned dashboards */}
          {favoriteDashboardItems?.length > 0 && (
            <Grid item xs={5}>
              <div className="fixed-favorite-db">
                <Grid
                  container
                  spacing={4}
                  columns={12}
                  direction="row"
                  justifyContent="flex-start"
                  alignContent="center"
                  alignItems="center"
                >
                  <Grid item xs={12}>
                    <div className="pinned-db-title">Pinned Dashboards</div>
                  </Grid>
                  <div className="landing-page-app-folders">
                    {favoriteDashboardItems.map((item, index) => {
                      return (
                        <Grid
                          item
                          xs={12}
                          alignContent="flex-start"
                          key={index}
                          className={`add-item-animation ${animationClassFavorite} ${
                            lastFavoriteItem !== item?.value &&
                            lastFavoriteItem !== ''
                              ? 'show'
                              : ''
                          }`}
                          style={{
                            transitionDelay: `${index * 100}ms`,
                          }}
                        >
                          <div
                            className="pinned-folder-item"
                            style={getTopPinnedFolder(index)}
                          >
                            <Box className="item-block-head">
                              <div
                                onClick={() => openDashboard(item)}
                                className="favorite-page-title addHover"
                              >
                                {item?.text}
                              </div>
                              <div
                                className="unpin-Dashboard addHover"
                                onClick={() => markFavorites(item)}
                              ></div>
                            </Box>
                            <div
                              onClick={() => openDashboard(item)}
                              className="landing-page-favorite-dashboard-bkg addHover"
                            >
                              <img
                                className="landing-page-favorite-dashboard-bkg-img"
                                src={getDashboardBackgroundImg(item)}
                                alt="open"
                              />
                            </div>
                            <div
                              className="gradientShadow"
                              // className={`${index === favoriteDashboardItems.length - 1 ? '' : 'gradientShadow'}`}
                              onClick={() => openDashboard(item)}
                            >
                              {' '}
                            </div>
                          </div>
                        </Grid>
                      );
                    })}
                  </div>
                </Grid>
              </div>
            </Grid>
          )}
        </Grid>
      </div>
      <AIAssistant showFabIcon={true} />
    </div>
  );
}

export default LandingPage;
